'use client';

import { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { usePartnerBranding } from '@/hooks/usePartnerBranding';
import BrandLogoSwap from '@/components/partner/BrandLogoSwap';

/**
 * Onboarding-side wrapper for the shared <BrandLogoSwap> — pulls partner
 * branding from `usePartnerBranding()` and forwards the URL so the swap
 * cross-fades between the 42A SVG and the partner image whenever it resolves.
 */
export function BrandedLogo({ width, height }: { width: number; height: number }) {
  const branding = usePartnerBranding();
  return <BrandLogoSwap partnerLogoUrl={branding?.logoUrl} partnerLogoDarkUrl={branding?.logoDarkUrl} width={width} height={height} />;
}

function FeatureItem({ color, title, description }: { color: string; title: string; description: string }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ background: color }} />
      <div>
        <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{title}</p>
        <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>{description}</p>
      </div>
    </div>
  );
}

/**
 * The shared onboarding branding sidebar. Rendered identically by
 * `OnboardingLayout` (the real onboarding screens) and the loading/redirect
 * states (`OnboardingLoadingScreen`, `ConditionalLayout`, `page.tsx`) so the
 * sidebar never disappears between sign-up and the first onboarding screen.
 *
 * `footerSlot` renders the org/theme/sign-out controls — omitted on loading
 * screens where org context may not be resolved yet.
 */
export default function OnboardingBrandingPanel({ footerSlot }: { footerSlot?: ReactNode }) {
  const t = useTranslations('onboarding.brandingPanel');
  return (
    <div
      className="hidden lg:flex flex-col justify-between overflow-hidden flex-shrink-0"
      style={{
        width: '520px',
        background: 'var(--color-surface)',
        borderRight: '1px solid var(--color-border)',
        position: 'relative',
      }}
    >
      {/* Gradient accent line at top */}
      <div className="absolute top-0 left-0 right-0 h-[2px] z-10" style={{ background: 'var(--gradient-lime-cyan)' }} />

      {/* Background gradient orbs */}
      <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full blur-[100px] pointer-events-none" style={{ background: 'var(--color-lime)', opacity: 0.3 }} />
      <div className="absolute top-1/2 -translate-y-1/2 -left-16 w-64 h-64 rounded-full blur-[100px] pointer-events-none" style={{ background: 'var(--color-purple)', opacity: 0.12 }} />
      <div className="absolute -bottom-24 -right-24 w-80 h-80 rounded-full blur-[100px] pointer-events-none" style={{ background: 'var(--color-cyan)', opacity: 0.2 }} />

      {/* Top: Logo + tagline */}
      <div className="relative z-10 p-10">
        <div className="mb-8">
          <BrandedLogo width={120} height={45} />
        </div>
        <h1 className="text-3xl xl:text-4xl font-bold leading-tight mb-4" style={{ color: 'var(--color-text-primary)' }}>
          {t('headline')}{' '}
          <span style={{ background: 'var(--gradient-lime-cyan)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            {t('headlineAccent')}
          </span>
        </h1>
        <p className="text-base leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
          {t('subheadline')}
        </p>
      </div>

      {/* Feature highlights */}
      <div className="relative z-10 p-10 space-y-5">
        <FeatureItem color="var(--color-lime)" title={t('features.aiDetection.title')} description={t('features.aiDetection.description')} />
        <FeatureItem color="var(--color-cyan)" title={t('features.competitorDiscovery.title')} description={t('features.competitorDiscovery.description')} />
        <FeatureItem color="var(--color-purple)" title={t('features.customPrompts.title')} description={t('features.customPrompts.description')} />
        <FeatureItem color="var(--color-cyan)" title={t('features.agentReadiness.title')} description={t('features.agentReadiness.description')} />
        <FeatureItem color="var(--color-lime)" title={t('features.tailoredInteractions.title')} description={t('features.tailoredInteractions.description')} />
      </div>

      {/* Bottom: controls (optional) + footer */}
      <div className="relative z-10 p-10 space-y-4">
        {footerSlot}
        <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
          {t('footer')}
        </p>
      </div>
    </div>
  );
}
